import { isCombinedGlobalInstanceMirrorFromTransform } from "building-types/components/componentInstanceUtils";
import type { ComponentTransform } from "building-types/components/componentTypes";
import { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { DoorDescriptor } from "building-types/doors/doorTypes";
import type { OpeningNoExprDescriptor } from "building-types/openings/opening";
import { OpeningHorizontalAlignment } from "building-types/openings/opening";
import type {
  ManualOpeningPlacement,
  OpeningPlacementReturnValue,
  RoomTypeOpeningPlacement,
} from "building-types/openings/openingPlacementTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { EvaluatedRoof } from "building-types/roofs/roofSystemTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { WindowDescriptor } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { assertIsArray, assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import { levelInchesNew } from "common/utils/nominalNumbers";
import getMergedDoorSettings from "doc/elements/doors/getMergedDoorSettings";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { entityParamFromSpecificWallLayer } from "doc/elements/openings/openingWallLayersUtils";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { roomIsEffectivelyExterior } from "doc/rooms/roomQuery";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import type { EdgeRef } from "geom/polygonPlanarGraph";
import { polyLine2Edge, polyLine2EdgeTangent } from "geom/polyLine2";
import type { UVTransformation } from "geom/uv";
import {
  doorDescriptorComputeHeight,
  generatedDoorPlacementDeprecatedNew,
  generatedDoorPlacementV2New,
} from "../elements/doors/door";
import { computeOpeningZOffset } from "../elements/openings/openingPlacement";
import type { OpeningPlacementFuncOptions } from "../elements/openings/openingPlacementFuncOptions";
import { doorPlacementFunc } from "../elements/openings/openingPlacementFuncs/doorPlacementFunc";
import { garageDoorPlacementFunc } from "../elements/openings/openingPlacementFuncs/garageDoorPlacementFunc";
import { windowPlacementFunc } from "../elements/openings/openingPlacementFuncs/windowPlacementFunc";
import {
  doorSwingsToOppositeWall,
  locateHalfEdgeAtWallStripParam,
  locateHalfEdgeAtWallStripParamForDeprecatedOpenings,
  openingPlacementFuncOptionsInvert,
  swingSideMirror,
} from "../elements/openings/openingUtils";
import {
  extendIntervalsBackwardToAdjacentCollinearEdges,
  extendIntervalsForwardToAdjacentCollinearEdges,
  getFromOrAddToWallLayerExtentsCache,
  getSubWalls,
  intervalFitsSomewhereInIntervalArray,
} from "../elements/openings/openingWallLayers";
import { resolveWindowPlacementHeadHeight } from "../elements/openings/resolveWindowPlacementHeadHeight";
import { generatedWindowPlacementDeprecatedNew, generatedWindowPlacementNew } from "../elements/window/windowTools";

/**
 * Combine global mirror with accumulated component instance mirrors for material transformation
 */
export function materialTransformationWithMirrorFallback(
  materialTransformation: UVTransformation | undefined,
  globalMirror: boolean,
  componentTransform?: ComponentTransform,
): UVTransformation | undefined {
  if (!materialTransformation) {
    return undefined;
  }
  if (materialTransformation.isMirrored !== undefined) {
    return materialTransformation;
  }
  return {
    ...materialTransformation,
    isMirrored: isCombinedGlobalInstanceMirrorFromTransform(globalMirror, componentTransform),
  };
}

/**
 * Combine global mirror with accumulated component instance mirrors
 */
export function isCombinedGlobalInstanceMirror(
  globalMirror: boolean,
  hostEntity: Room | EvaluatedRoof | GeneratedRoof | undefined,
): boolean {
  return isCombinedGlobalInstanceMirrorFromTransform(globalMirror, hostEntity?.componentTransform);
}

/**
 * Combine global mirror with per-axis accumulated x and y flips
 * The xor should match isCombinedGlobalInstanceMirror
 */
export function getCombinedMirrorAxes(
  globalMirror: boolean,
  componentTransform: ComponentTransform | undefined,
): { combinedFlipX: boolean; combinedFlipY: boolean } {
  const instanceMirrorX = componentTransform?.mirrorX ?? false;
  const instanceMirrorY = componentTransform?.mirrorY ?? false;
  return { combinedFlipX: globalMirror !== instanceMirrorX, combinedFlipY: instanceMirrorY };
}

/**
 * Adjusts a room edge param to account for justification to right, left, or center.
 */
function openingParamJustificationAdjustment(
  openingWidth: number,
  horizontalAlignment: OpeningHorizontalAlignment,
  mirror: boolean,
) {
  const halfWidth = openingWidth / 2;
  switch (horizontalAlignment) {
    case OpeningHorizontalAlignment.CENTER: {
      return 0;
    }
    case OpeningHorizontalAlignment.RIGHT: {
      return mirror ? -halfWidth : halfWidth;
    }
    case OpeningHorizontalAlignment.LEFT: {
      return mirror ? halfWidth : -halfWidth;
    }
  }
}

/**
 * Room Type openings are openings that are invoked when rooms of specific types border each other
 */
export function resolveRoomTypeOpenings(openingPlacement: RoomTypeOpeningPlacement, opts: OpeningPlacementFuncOptions) {
  let res: OpeningPlacementReturnValue | undefined | null;

  const options = openingPlacement.options;

  switch (options.funcType) {
    case "DOOR": {
      res = doorPlacementFunc({ ...options, ...{ opts } });
      break;
    }
    case "WINDOW": {
      res = windowPlacementFunc(opts);
      break;
    }
    case "GARAGE_DOOR": {
      res = garageDoorPlacementFunc({ ...options, ...{ opts } });
      break;
    }
  }
  return res;
}

interface ResolveManualOpeningsParams {
  placement: ManualOpeningPlacement;
  opts: OpeningPlacementFuncOptions;
  windowCatalog: Table<WindowDescriptor>;
  doorCatalog: Table<DoorDescriptor>;
  mirror: boolean;
  issues: BuildingGenerationIssue[];
  wallMasses: Table<WallMassSystem>;
  wallLayerExtentsCache: WallLayerExtentsCache;
  levelRooms: Room[];
}

/**
 * Manual openings are openings that have been placed by a user manual inserting by room edge
 */
export function resolveManualOpenings({
  placement,
  opts,
  windowCatalog,
  doorCatalog,
  mirror,
  issues,
  wallMasses,
  wallLayerExtentsCache,
  levelRooms,
}: ResolveManualOpeningsParams): OpeningPlacementReturnValue {
  if (!(windowCatalog)) {
    console.assert(false, "window catalog must be provided");
  }
  if (!(doorCatalog)) {
    console.assert(false, "door catalog must be provided");
  }

  const res: OpeningPlacementReturnValue = { doorPlacements: [], windowPlacements: [] };
  const { entitySettings, activeSettingsSets, settings, optionChoices } = opts.mergeSettingsForOpeningParams;

  const hasNonVoidSubWalls = (subWalls: EdgeRef[], wallMassSystem: WallMassSystem, planarGraph: PlanarGraph) => {
    for (let subWallIndex = 0, subWallCount = subWalls?.length; subWallIndex < subWallCount; subWallIndex++) {
      const { index, edgeIndex } = subWalls[subWallIndex];
      const halfEdgeIndex = planarGraph.faceHalfEdges[index][edgeIndex];
      const wallId = wallMassSystem.halfEdgeToWall[halfEdgeIndex];
      const wall = wallMassSystem.walls[wallId];
      if (wall.wallType !== WallType.VOID) {
        return true;
      }
    }
    return false;
  };

  for (const openingIndex in placement.openings) {
    const opening: OpeningNoExprDescriptor = placement.openings[openingIndex];
    const openingHandle = opening.handle ?? { type: "OPENING", id: opening.id };

    switch (opening.type) {
      case "WINDOW": {
        const descriptor = windowCatalog[opening.windowType];

        if (!descriptor) {
          issues.push({
            type: BuildingGenerationIssueType.UNKNOWN_WINDOW_TYPE,
            windowType: opening.windowType,
            handle: openingHandle,
          });
          continue;
        }

        if (
          !opening.wallStripDomain &&
          opts.roomStart &&
          opts.roomEnd &&
          !isWalledRoomEnclosure[opts.roomStart.attributes.roomEnclosure]
        ) {
          opts = openingPlacementFuncOptionsInvert(opts);
        }

        const { roomStart, levelAttributes, wallIndex, level } = opts;
        let roomEnd = opts.roomEnd;
        let { halfEdgeIndex } = opts;

        const param = mirror ? -opening.param : opening.param;

        if (opening.wallStripDomain) {
          assertIsNotNullOrUndefined(roomStart);

          const res = locateHalfEdgeAtWallStripParamForDeprecatedOpenings(
            roomStart,
            wallIndex,
            param,
            opening.paramNormalized,
            opts.roomIndex,
          );

          if (!res) {
            // out of bounds
            continue;
          }
          halfEdgeIndex = res.halfEdgeIndex;
          roomEnd = res.roomEnd;
        }

        let frontExterior = roomIsEffectivelyExterior(roomStart, false);
        const backExterior = roomIsEffectivelyExterior(roomEnd, false);

        if (frontExterior === backExterior) {
          frontExterior = roomIsEffectivelyExterior(roomStart, true);
        }

        const reverse = opening.wallStripDomain && frontExterior;

        const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdgeIndex);
        const streetOrientation = opts.planarGraphPerimeter.coveredPerimeterStreetOrientations[oppHalfEdge];

        const { exteriorCasing, interiorCasing, framing, wrapping, windowHeadHeight } = getMergedWindowSettings({
          homeSettings: opts.windowHomeSettings,
          activeSettingsSets,
          window: opening,
          entitySettings,
          optionChoices,
          descriptor,
          settings,
        });

        //adjusted head height
        const headHeightAdjusted = resolveWindowPlacementHeadHeight(
          opening,
          level,
          levelAttributes,
          descriptor,
          windowHeadHeight,
        );

        assertIsArray(res.windowPlacements);

        assertIsNotNullOrUndefined(roomStart);

        res.windowPlacements.push(
          generatedWindowPlacementDeprecatedNew({
            id: opening.id,
            descriptor,
            frontRoom: roomStart,
            backRoom: roomEnd,
            edgeId: opening.edgeId,
            halfEdgeIndex,
            param,
            paramNormalized: opening.paramNormalized,
            paramAnchor: opening.paramAnchor,
            headHeight: headHeightAdjusted,
            opening: undefined,
            verticalOffset: opening.verticalOffset ?? 0,
            lateralDatum: opening.lateralDatum,
            lateralAlignment: opening.lateralAlignment,
            lateralOffset: opening.lateralOffset,
            materials: opening.materials,
            awning: opening.awning,
            awningSettings: opening.awningSettings,
            framing,
            exteriorCasing,
            interiorCasing,
            wrapping,
            mullions: opening.mullions,
            shutters: opening.shutters,
            operationType: opening.operationType,
            wallStripDomain: opening.wallStripDomain,
            reverse,
            handle: openingHandle,
            handleAuxiliary: opening.handleAuxiliary,
            reportedName: opening.reportedName,
            tagDetails: opening.tagDetails,
            tagOffset: opening.tagOffset,
            viewNotes: opening.viewNotes,
            disableAutoDimensions: opening.disableAutoDimensions,
            paneSplitParam: opening.paneSplitParam,
            paneSplitParamNormalized: opening.paneSplitParamNormalized,
            verticalDivisionSpacing: opening.verticalDivisionSpacing,
            entityInstanceType: opening.entityInstanceType,
            streetOrientation,
          }),
        );

        break;
      }
      case "DOOR": {
        if (opening.doorType === BuiltinDoorId.NONE) {
          continue;
        }

        const doorDescriptor0 = doorCatalog[opening.doorType];

        if (!doorDescriptor0) {
          issues.push({
            type: BuildingGenerationIssueType.UNKNOWN_DOOR_TYPE,
            doorType: opening.doorType,
            handle: openingHandle,
          });
          continue;
        }

        let doReverse = false;

        if (opening.reverse) {
          // wall strip domain doors should not invert do openingPlacementFuncOptionsInvert,
          // but instead handle reverse later in pipeline as a overall behavior improvement
          if (opening.wallStripDomain) {
            doReverse = true;
          } else {
            opts = openingPlacementFuncOptionsInvert(opts);
          }
        } else if (
          doorDescriptor0.requiresDoorSwing &&
          !opening.ignoreOppositeWallCollision &&
          doorSwingsToOppositeWall(opts, doorDescriptor0) &&
          doorDescriptor0.operationType !== DoorOperationType.GARAGE_DOUBLE &&
          doorDescriptor0.operationType !== DoorOperationType.GARAGE_SINGLE
        ) {
          if (opening.wallStripDomain) {
            doReverse = true;
          } else {
            opts = openingPlacementFuncOptionsInvert(opts);
          }
        }

        const { roomStart, wallIndex } = opts;
        let { roomEnd, halfEdgeIndex } = opts;

        const param = mirror ? -opening.param : opening.param;

        if (opening.wallStripDomain) {
          assertIsNotUndefined(roomStart);

          const res = locateHalfEdgeAtWallStripParamForDeprecatedOpenings(
            roomStart,
            wallIndex,
            param,
            opening.paramNormalized,
            opts.roomIndex,
          );

          if (res === null) {
            continue;
          }

          // if res returns `undefined` due to subwalls not being found, door can still be placed but it will not receive the halfEdge and roomEnd.
          if (res) {
            halfEdgeIndex = res.halfEdgeIndex;
            roomEnd = res.roomEnd;
          }
        }

        const doorBaseOffset = computeOpeningZOffset(opts.roomFloorAssemblies, roomStart, roomEnd);
        const descriptor = doorDescriptorComputeHeight(doorDescriptor0, doorBaseOffset, opts.doorHomeSettings.door);

        if (!descriptor.height || isNaN(descriptor.height)) {
          issues.push({
            type: BuildingGenerationIssueType.INVALID_DOOR_HEIGHT,
            doorType: opening.doorType,
            handle: openingHandle,
            message: `Descriptor height is "${doorDescriptor0.height}", Computed door height is "${descriptor.height}"`,
          });
          continue;
        }

        const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdgeIndex);
        const streetOrientation = opts.planarGraphPerimeter.coveredPerimeterStreetOrientations[oppHalfEdge];

        const { exteriorCasing, framing, wrapping } = getMergedDoorSettings({
          homeSettings: opts.doorHomeSettings,
          activeSettingsSets,
          entitySettings,
          optionChoices,
          door: opening,
          descriptor,
          settings,
        });

        assertIsArray(res.doorPlacements);

        /**
         * The frontRoom should always be defined, but in rare cases is not.
         * These doors are purposely omitted as they cause many strict
         * null errors but don't impact geometry of the building.
         */
        if (!roomStart) {
          continue;
        }

        res.doorPlacements.push(
          generatedDoorPlacementDeprecatedNew({
            descriptor,
            frontRoom: roomStart,
            backRoom: roomEnd,
            halfEdgeIndex,
            edgeId: opening.edgeId,
            param,
            paramNormalized: opening.paramNormalized,
            verticalOffset: opening.verticalOffset,
            lateralOffset: opening.lateralOffset,
            swingSide: mirror ? swingSideMirror(opening.swingSide) : opening.swingSide,
            awning: opening.awning,
            awningSettings: opening.awningSettings,
            exteriorCasing,
            interiorCasing: opening.interiorCasing,
            framing,
            wrapping,
            includeDriveway: opening.includeDriveway,
            wallStripDomain: opening.wallStripDomain,
            reverse: doReverse,
            useStripLimits: opening.useStripLimits,
            handle: openingHandle,
            handleAuxiliary: opening.handleAuxiliary,
            reportedName: opening.reportedName,
            tagOffset: opening.tagOffset,
            tagDetails: opening.tagDetails,
            viewNotes: opening.viewNotes,
            disableAutoDimensions: opening.disableAutoDimensions,
            entityInstanceType: opening.entityInstanceType,
            streetOrientation,
          }),
        );

        break;
      }
      case "WINDOW_V2": {
        const descriptor = windowCatalog[opening.windowType];

        if (!descriptor) {
          issues.push({
            type: BuildingGenerationIssueType.UNKNOWN_WINDOW_TYPE,
            windowType: opening.windowType,
            handle: openingHandle,
          });
          continue;
        }
        let halfEdgeIndex = opts.halfEdgeIndex;

        const { roomStart: room, wallIndex: roomEdgeIndex, roomIndex, level, levelAttributes } = opts;
        assertIsNotUndefined(room);

        const roomEdge = polyLine2Edge(room.outline, roomEdgeIndex);
        if (!roomEdge) {
          continue;
        }

        const subwalls = getSubWalls(roomIndex, room, roomEdgeIndex);
        if (!subwalls) {
          continue;
        }

        const polygonPlanarGraph = roomIndex.planarGraphs[room.level.id];
        const wallMassSystem = wallMasses[room.level.id];

        //do not proceed if all the subwalls are void
        if (!hasNonVoidSubWalls(subwalls, wallMassSystem, polygonPlanarGraph.planarGraph)) {
          continue;
        }

        const wallLayerExtents = getFromOrAddToWallLayerExtentsCache(
          room,
          roomEdgeIndex,
          wallLayerExtentsCache,
          roomIndex,
          wallMassSystem,
        );

        const {
          edgeMinTable,
          edgeMaxTable,
          largestInsideOffset,
          mostInwardLayerIntoRoom,
          intervalsWhereAnOpeningCanFit,
          roomEdgeLength: edgeLength,
          firstInsideOffset,
          firstOutsideOffset,
          lastInsideOffset,
          lastOutsideOffset,
        } = wallLayerExtents;

        if (intervalsWhereAnOpeningCanFit.length < 2) {
          // can't place this opening anywhere in this wall
          issues.push({
            type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
            handle: openingHandle,
          });
          continue;
        }

        const intervalsCopy = [...intervalsWhereAnOpeningCanFit];

        const [tx, ty] = polyLine2EdgeTangent(room.outline, roomEdgeIndex);

        // windows can overlap adjacent collinear room edges, so we need to check forwards
        extendIntervalsForwardToAdjacentCollinearEdges(
          intervalsCopy,
          edgeLength,
          roomEdgeIndex,
          tx,
          ty,
          room,
          wallLayerExtentsCache,
          roomIndex,
          wallMassSystem,
          lastInsideOffset,
          lastOutsideOffset,
        );

        // windows can overlap adjacent collinear room edges, so we need to check backwards
        extendIntervalsBackwardToAdjacentCollinearEdges(
          intervalsCopy,
          roomEdgeIndex,
          tx,
          ty,
          room,
          wallLayerExtentsCache,
          roomIndex,
          wallMassSystem,
          firstInsideOffset,
          firstOutsideOffset,
        );

        const { param: tempParam, layerLength } = entityParamFromSpecificWallLayer({
          placement: opening,
          largestOffset: largestInsideOffset,
          edgeLength,
          mirror,
          mostInwardLayerIntoRoom,
          edgeMinTable,
          edgeMaxTable,
        });
        const param =
          tempParam + openingParamJustificationAdjustment(descriptor.width, opening.horizontalAlignment, mirror);

        const { fits, paramAdjustmentRequiredToFit } = intervalFitsSomewhereInIntervalArray(
          param,
          descriptor.width,
          intervalsCopy,
        );

        if (!fits) {
          if (paramAdjustmentRequiredToFit === null) {
            issues.push({
              type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
              handle: openingHandle,
            });
            continue;
          }
          // will force param to denormalized value in building issue panel
          const formattedClosestParamThatFits =
            (opening.paramNormalized ? opening.param * layerLength : opening.param) + paramAdjustmentRequiredToFit;

          issues.push({
            type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
            handle: openingHandle,
            paramAdjustment: formattedClosestParamThatFits,
          });
          continue;
        }

        const result = locateHalfEdgeAtWallStripParam(
          room,
          roomEdgeIndex,
          param,
          subwalls,
          polygonPlanarGraph,
          levelRooms,
        );
        if (!result) {
          /** It is rare that a WINDOW_V2 would be caught here as the placement algorithm is more
           * aware of the wall context. It should be caught above in the interval checks.
           */
          issues.push({
            type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
            handle: openingHandle,
          });
          continue;
        }
        halfEdgeIndex = result.halfEdgeIndex;
        const roomEnd = result.roomEnd;

        let frontExterior = roomIsEffectivelyExterior(room, false);
        const backExterior = roomIsEffectivelyExterior(roomEnd, false);

        if (frontExterior === backExterior) {
          frontExterior = roomIsEffectivelyExterior(room, true);
        }

        const reverse = frontExterior;

        const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdgeIndex);
        const streetOrientation = opts.planarGraphPerimeter.coveredPerimeterStreetOrientations[oppHalfEdge];

        const { exteriorCasing, interiorCasing, framing, wrapping, windowHeadHeight } = getMergedWindowSettings({
          homeSettings: opts.windowHomeSettings,
          activeSettingsSets,
          window: opening,
          entitySettings,
          optionChoices,
          descriptor,
          settings,
        });

        //adjusted head height
        const headHeightAdjusted = resolveWindowPlacementHeadHeight(
          opening,
          level,
          levelAttributes,
          descriptor,
          windowHeadHeight,
        );

        assertIsArray(res.windowPlacements);

        res.windowPlacements.push(
          generatedWindowPlacementNew({
            id: opening.id,
            descriptor,
            frontRoom: room,
            backRoom: roomEnd,
            halfEdgeIndex,
            edgeId: opening.edgeId,
            param,
            paramNormalized: false,
            paramAnchor: opening.paramAnchor,
            headHeight: headHeightAdjusted,
            opening: undefined,
            verticalOffset: opening.verticalOffset ?? 0,
            lateralDatum: opening.lateralDatum,
            lateralAlignment: opening.lateralAlignment,
            lateralOffset: opening.lateralOffset,
            materials: opening.materials,
            awning: opening.awning,
            framing,
            wrapping,
            awningSettings: opening.awningSettings,
            exteriorCasing,
            interiorCasing,
            mullions: opening.mullions,
            shutters: opening.shutters,
            operationType: opening.operationType,
            reverse,
            handle: openingHandle,
            handleAuxiliary: opening.handleAuxiliary,
            reportedName: opening.reportedName,
            tagDetails: opening.tagDetails,
            tagOffset: opening.tagOffset,
            viewNotes: opening.viewNotes,
            x: result.x,
            y: result.y,
            disableAutoDimensions: opening.disableAutoDimensions,
            paneSplitParam: opening.paneSplitParam,
            paneSplitParamNormalized: opening.paneSplitParamNormalized,
            verticalDivisionSpacing: opening.verticalDivisionSpacing,
            entityInstanceType: opening.entityInstanceType,
            streetOrientation,
            halfEdgeParam: result.t,
          }),
        );

        break;
      }
      case "DOOR_V2": {
        if (opening.doorType === BuiltinDoorId.NONE) {
          continue;
        }

        const descriptor = doorCatalog[opening.doorType];

        if (!descriptor) {
          issues.push({
            type: BuildingGenerationIssueType.UNKNOWN_DOOR_TYPE,
            doorType: opening.doorType,
            handle: openingHandle,
          });
          continue;
        }

        let halfEdgeIndex = opts.halfEdgeIndex;

        const { roomStart: room, wallIndex: roomEdgeIndex, roomIndex } = opts;

        assertIsNotUndefined(room);
        const roomEdge = polyLine2Edge(room.outline, roomEdgeIndex);
        if (!roomEdge) {
          continue;
        }

        const polygonPlanarGraph = roomIndex.planarGraphs[room.level.id];
        const wallMassSystem = wallMasses[room.level.id];
        const subwalls = getSubWalls(roomIndex, room, roomEdgeIndex);
        if (!subwalls) {
          continue;
        }

        //do not proceed if all the subwalls are void
        if (!hasNonVoidSubWalls(subwalls, wallMassSystem, polygonPlanarGraph.planarGraph)) {
          continue;
        }

        const wallLayerExtents = getFromOrAddToWallLayerExtentsCache(
          room,
          roomEdgeIndex,
          wallLayerExtentsCache,
          roomIndex,
          wallMassSystem,
        );

        const {
          edgeMinTable,
          edgeMaxTable,
          largestInsideOffset,
          mostInwardLayerIntoRoom: mostInwardLayerIntoRoom,
          intervalsWhereAnOpeningCanFit,
          roomEdgeLength: edgeLength,
          lastInsideOffset,
          lastOutsideOffset,
          firstInsideOffset,
          firstOutsideOffset,
        } = wallLayerExtents;

        if (intervalsWhereAnOpeningCanFit.length < 2) {
          // can't place this opening anywhere in this wall
          issues.push({
            type: BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY,
            handle: openingHandle,
          });
          continue;
        }

        const intervalsCopy = [...intervalsWhereAnOpeningCanFit];

        const [tx, ty] = polyLine2EdgeTangent(room.outline, roomEdgeIndex);

        // doors can overlap adjacent collinear room edges, so we need to check forwards
        extendIntervalsForwardToAdjacentCollinearEdges(
          intervalsCopy,
          edgeLength,
          roomEdgeIndex,
          tx,
          ty,
          room,
          wallLayerExtentsCache,
          roomIndex,
          wallMassSystem,
          lastInsideOffset,
          lastOutsideOffset,
        );

        // doors can overlap adjacent collinear room edges, so we need to check backwards
        extendIntervalsBackwardToAdjacentCollinearEdges(
          intervalsCopy,
          roomEdgeIndex,
          tx,
          ty,
          room,
          wallLayerExtentsCache,
          roomIndex,
          wallMassSystem,
          firstInsideOffset,
          firstOutsideOffset,
        );

        const { param: tempParam, layerLength } = entityParamFromSpecificWallLayer({
          placement: opening,
          largestOffset: largestInsideOffset,
          edgeLength,
          mirror,
          mostInwardLayerIntoRoom,
          edgeMinTable,
          edgeMaxTable,
        });
        const param =
          tempParam + openingParamJustificationAdjustment(descriptor.width, opening.horizontalAlignment, mirror);

        let widthForFitCheck = descriptor.width;
        switch (descriptor.id) {
          case BuiltinDoorId.OPEN_FULL:
          case BuiltinDoorId.OPEN_ARC_FULL:
          case BuiltinDoorId.CL_BYPASS: {
            widthForFitCheck = levelInchesNew(edgeLength - 6);
            break;
          }
        }

        const { fits, paramAdjustmentRequiredToFit } = intervalFitsSomewhereInIntervalArray(
          param,
          widthForFitCheck,
          intervalsCopy,
        );

        if (!fits && opening.doorType !== BuiltinDoorId.OPEN_FULL) {
          if (paramAdjustmentRequiredToFit === null) {
            issues.push({
              type: BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY,
              handle: openingHandle,
            });
            continue;
          }

          // will force param to denormalized value in building issue panel
          const formattedClosestParamThatFits =
            (opening.paramNormalized ? opening.param * layerLength : opening.param) + paramAdjustmentRequiredToFit;

          issues.push({
            type: BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY,
            handle: openingHandle,
            paramAdjustment: formattedClosestParamThatFits,
          });
          continue;
        }

        const result = locateHalfEdgeAtWallStripParam(
          room,
          roomEdgeIndex,
          param,
          subwalls,
          polygonPlanarGraph,
          levelRooms,
        );
        if (!result) {
          /** It is rare that a DOOR_V2 would be caught here as the placement algorithm is more
           * aware of the wall context. It should be caught above in the interval checks.
           */
          issues.push({
            type: BuildingGenerationIssueType.DOOR_OPENING_EXCEEDS_BOUNDARY,
            handle: openingHandle,
          });
          continue;
        }
        halfEdgeIndex = result.halfEdgeIndex;
        const roomEnd = result.roomEnd;

        const doorBaseOffset = computeOpeningZOffset(opts.roomFloorAssemblies, room, roomEnd);
        const doorDescriptor = doorDescriptorComputeHeight(descriptor, doorBaseOffset, opts.doorHomeSettings.door);

        if (!doorDescriptor.height || isNaN(doorDescriptor.height)) {
          issues.push({
            type: BuildingGenerationIssueType.INVALID_DOOR_HEIGHT,
            doorType: opening.doorType,
            handle: openingHandle,
            message: `Descriptor height is "${descriptor.height}", Computed door height is "${doorDescriptor.height}"`,
          });
          continue;
        }

        const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdgeIndex);
        const streetOrientation = opts.planarGraphPerimeter.coveredPerimeterStreetOrientations[oppHalfEdge];

        const { exteriorCasing, framing, wrapping, swingSide } = getMergedDoorSettings({
          homeSettings: opts.doorHomeSettings,
          descriptor: doorDescriptor,
          activeSettingsSets,
          entitySettings,
          door: opening,
          optionChoices,
          settings,
        });

        assertIsArray(res.doorPlacements);
        res.doorPlacements.push(
          generatedDoorPlacementV2New({
            descriptor: doorDescriptor,
            frontRoom: room,
            backRoom: roomEnd,
            halfEdgeIndex,
            param,
            paramNormalized: opening.paramNormalized,
            verticalOffset: opening.verticalOffset,
            lateralOffset: opening.lateralOffset,
            swingSide: mirror ? swingSideMirror(swingSide) : swingSide,
            awning: opening.awning,
            framing,
            wrapping,
            awningSettings: opening.awningSettings,
            exteriorCasing,
            interiorCasing: opening.interiorCasing,
            includeDriveway: opening.includeDriveway,
            reverse: opening.reverse,
            handle: openingHandle,
            handleAuxiliary: opening.handleAuxiliary,
            reportedName: opening.reportedName,
            tagOffset: opening.tagOffset,
            tagDetails: opening.tagDetails,
            viewNotes: opening.viewNotes,
            disableAutoDimensions: opening.disableAutoDimensions,
            x: result.x,
            y: result.y,
            entityInstanceType: opening.entityInstanceType,
            streetOrientation,
            halfEdgeParam: result.t,
          }),
        );
        break;
      }
    }
  }

  return res;
}
